import BigNumber from 'bignumber.js'
import * as DateF from 'date-fns'

import { Array, pipe, Result, Set } from '@blade/std-lib'
import { Provider } from '@root/entities/provider'
import { Socials } from '@root/entities/socials/model'
import { DomUtil, NumberUtil } from '@root/shared'

import type { SupportedDex } from '@blade/blockchain-types'
import type { Equivalence } from '@blade/std-lib'
import type {
  ServicesPoolsGetCookingPoolsCreatePayload,
  ServicesPoolsGetNewCreatePayload,
} from '@blade/trade-api-client'
import type { Ctx } from '@reatom/framework'
import type { PairMetrics, PairModel } from '@root/entities/pair'

// We do not want to import enums from blockchain-types to avoid schema dependencies in bundle
export type PoolsSortBy = NonNullable<
  NonNullable<ServicesPoolsGetNewCreatePayload['filters']>['sortBy']
>
type ExplorePoolFiltersDto = NonNullable<
  ServicesPoolsGetCookingPoolsCreatePayload['filters']
>

export const EMPTY: ExploreFilter = {
  keywords: [],
  maxLiquidityInUsd: undefined,
  minLiquidityInUsd: undefined,
  maxVolumeInUsd: undefined,
  minVolumeInUsd: undefined,
  maxMarketCap: undefined,
  minMarketCap: undefined,
  maxHolders: undefined,
  minHolders: undefined,
  maxBoosters: undefined,
  minBoosters: undefined,
  maxSplashPoints: undefined,
  minSplashPoints: undefined,
  minTx: undefined,
  maxTx: undefined,
  minDevHolding: undefined,
  maxDevHolding: undefined,
  minTop10Holding: undefined,
  maxTop10Holding: undefined,
  minInsidersHolding: undefined,
  maxInsidersHolding: undefined,
  minSnipersHolding: undefined,
  maxSnipersHolding: undefined,
  minBundlersHolding: undefined,
  maxBundlersHolding: undefined,
  providers: 'all',
  withMayhem: false,
  maxAge: undefined,
  minAge: undefined,
  minFee: undefined,
  maxFee: undefined,
  withAtLeastOneSocial: undefined,
  withTwitter: undefined,
  withTelegram: undefined,
  withWebsite: undefined,
}
export interface ExploreFilter {
  keywords: Array<string>
  maxLiquidityInUsd: BigNumber | undefined
  minLiquidityInUsd: BigNumber | undefined
  maxVolumeInUsd: BigNumber | undefined
  minVolumeInUsd: BigNumber | undefined
  maxMarketCap: BigNumber | undefined
  minMarketCap: BigNumber | undefined
  maxHolders: BigNumber | undefined
  minHolders: BigNumber | undefined
  maxTx: BigNumber | undefined
  minTx: BigNumber | undefined
  minAge: number | undefined
  maxAge: number | undefined
  maxBoosters: BigNumber | undefined
  minBoosters: BigNumber | undefined
  maxSplashPoints: BigNumber | undefined
  minSplashPoints: BigNumber | undefined
  minDevHolding: BigNumber | undefined
  maxDevHolding: BigNumber | undefined
  minTop10Holding: BigNumber | undefined
  maxTop10Holding: BigNumber | undefined
  minSnipersHolding: BigNumber | undefined
  maxSnipersHolding: BigNumber | undefined
  minInsidersHolding: BigNumber | undefined
  maxInsidersHolding: BigNumber | undefined
  minBundlersHolding: BigNumber | undefined
  maxBundlersHolding: BigNumber | undefined
  minFee: BigNumber | undefined
  maxFee: BigNumber | undefined
  providers: Set<Provider.Id> | 'all'
  withMayhem: boolean
  withAtLeastOneSocial: undefined | true
  withTwitter: undefined | true
  withTelegram: undefined | true
  withWebsite: undefined | true
}

export const isPairSatisfiesExploreFilter =
  (
    filter: ExploreFilter,
    poolPeriod: PairMetrics.MetricsSpan,
    referenceDate: Date,
  ) =>
  // eslint-disable-next-line sonarjs/cognitive-complexity
  (ctx: Ctx, pair: PairModel): boolean => {
    const ctxGetOrSpy = (ctx.spy ?? ctx.get).bind(ctx)

    const liquidityUsd = ctxGetOrSpy(pair.liquidityUsd)
    const marketCapUsd = ctxGetOrSpy(pair.marketCapUsd)
    const volumeUsd = ctxGetOrSpy(pair.stats[poolPeriod].volumeUsd)
    const boosters = ctxGetOrSpy(pair.premiumInfo.boosters)
    const splashPoints = ctxGetOrSpy(pair.premiumInfo.splashPoints)
    const holders = ctxGetOrSpy(pair.holdersCount)
    const top10Holding = ctxGetOrSpy(pair.top10holding)
    const devHolding = ctxGetOrSpy(pair.audit).checks.ownerTokenBalance.percent
    const txCount = ctxGetOrSpy(pair.stats[poolPeriod].transactions)
    const totalFees = ctxGetOrSpy(pair.fees.totalFees)
    const socials = ctxGetOrSpy(pair.quote.socials)
    const insidersHolding = ctxGetOrSpy(pair.insiders).holding
    const snipersHolding = ctxGetOrSpy(pair.snipers).holding
    const bundlersHolding = ctxGetOrSpy(pair.bundlers).holding
    const provider = pair.provider

    if (filter.maxLiquidityInUsd && liquidityUsd.gt(filter.maxLiquidityInUsd)) {
      return false
    }
    if (filter.minLiquidityInUsd && liquidityUsd.lt(filter.minLiquidityInUsd)) {
      return false
    }

    if (filter.maxMarketCap && marketCapUsd.gt(filter.maxMarketCap)) {
      return false
    }
    if (filter.minMarketCap && marketCapUsd.lt(filter.minMarketCap)) {
      return false
    }

    if (
      filter.minAge &&
      pair.createdAt &&
      Math.abs(DateF.differenceInSeconds(pair.createdAt, referenceDate) / 60) <
        filter.minAge
    ) {
      return false
    }
    if (
      filter.maxAge &&
      pair.createdAt &&
      Math.abs(DateF.differenceInSeconds(pair.createdAt, referenceDate) / 60) >
        filter.maxAge
    ) {
      return false
    }
    if (filter.maxVolumeInUsd && volumeUsd.gt(filter.maxVolumeInUsd)) {
      return false
    }
    if (filter.minVolumeInUsd && volumeUsd.lt(filter.minVolumeInUsd)) {
      return false
    }
    if (filter.maxBoosters && boosters > filter.maxBoosters.toNumber()) {
      return false
    }
    if (filter.minBoosters && boosters < filter.minBoosters.toNumber()) {
      return false
    }
    if (filter.minFee && totalFees.lt(filter.minFee)) {
      return false
    }
    if (filter.maxFee && totalFees.gt(filter.maxFee)) {
      return false
    }
    if (
      filter.maxSplashPoints &&
      splashPoints > filter.maxSplashPoints.toNumber()
    ) {
      return false
    }
    if (
      filter.minSplashPoints &&
      splashPoints < filter.minSplashPoints.toNumber()
    ) {
      return false
    }
    if (filter.maxHolders && holders > filter.maxHolders.toNumber()) {
      return false
    }
    if (filter.minHolders && holders < filter.minHolders.toNumber()) {
      return false
    }
    if (filter.maxTx && txCount > filter.maxTx.toNumber()) {
      return false
    }
    if (filter.minTx && txCount < filter.minTx.toNumber()) {
      return false
    }
    if (filter.minDevHolding && devHolding < filter.minDevHolding.toNumber()) {
      return false
    }
    if (filter.maxDevHolding && devHolding > filter.maxDevHolding.toNumber()) {
      return false
    }
    if (filter.minTop10Holding && top10Holding.lt(filter.minTop10Holding)) {
      return false
    }
    if (filter.maxTop10Holding && top10Holding.gt(filter.maxTop10Holding)) {
      return false
    }

    if (
      filter.minInsidersHolding &&
      insidersHolding.lt(filter.minInsidersHolding)
    ) {
      return false
    }
    if (
      filter.maxInsidersHolding &&
      insidersHolding.gt(filter.maxInsidersHolding)
    ) {
      return false
    }
    if (
      filter.minSnipersHolding &&
      snipersHolding.lt(filter.minSnipersHolding)
    ) {
      return false
    }
    if (
      filter.maxSnipersHolding &&
      snipersHolding.gt(filter.maxSnipersHolding)
    ) {
      return false
    }
    if (
      filter.minBundlersHolding &&
      bundlersHolding.lt(filter.minBundlersHolding)
    ) {
      return false
    }
    if (
      filter.maxBundlersHolding &&
      bundlersHolding.gt(filter.maxBundlersHolding)
    ) {
      return false
    }

    if (pair.provider === Provider.pumpFunMayhem) {
      if (!filter.withMayhem) {
        return false
      }
    } else {
      if (filter.providers !== 'all' && !filter.providers.has(provider.id)) {
        return false
      }
    }

    if (filter.withAtLeastOneSocial && !Socials.hasAtLeastOneSocial(socials)) {
      return false
    }
    if (filter.withTwitter && !socials.x && !socials.twitterInfo) {
      return false
    }
    if (filter.withTelegram && !socials.telegram) {
      return false
    }
    if (filter.withWebsite && !socials.website) {
      return false
    }

    const lowerKeyWords = filter.keywords.map((x) => x.toLowerCase())
    if (lowerKeyWords.length === 0) {
      return true
    }

    return [pair.quote.ticker, pair.quote.title]
      .map((x) => x.toLowerCase())
      .some((word) => lowerKeyWords.some((keyword) => word.includes(keyword)))
  }

export const toJSON = (filter: ExploreFilter): string => {
  return JSON.stringify({
    keywords: filter.keywords,
    maxLiquidityInUsd: filter.maxLiquidityInUsd?.toString(),
    minLiquidityInUsd: filter.minLiquidityInUsd?.toString(),
    maxVolumeInUsd: filter.maxVolumeInUsd?.toString(),
    minVolumeInUsd: filter.minVolumeInUsd?.toString(),
    maxMarketCap: filter.maxMarketCap?.toString(),
    minMarketCap: filter.minMarketCap?.toString(),
    maxHolders: filter.maxHolders?.toString(),
    minHolders: filter.minHolders?.toString(),
    minTx: filter.minTx?.toString(),
    maxTx: filter.maxTx?.toString(),
    minAge: filter.minAge?.toString(),
    maxAge: filter.maxAge?.toString(),
    maxBoosters: filter.maxBoosters?.toString(),
    minBoosters: filter.minBoosters?.toString(),
    maxSplashPoints: filter.maxSplashPoints?.toString(),
    minSplashPoints: filter.minSplashPoints?.toString(),
    minDevHolding: filter.minDevHolding?.toString(),
    maxDevHolding: filter.maxDevHolding?.toString(),
    minTop10Holding: filter.minTop10Holding?.toString(),
    maxTop10Holding: filter.maxTop10Holding?.toString(),
    minInsidersHolding: filter.minInsidersHolding?.toString(),
    maxInsidersHolding: filter.maxInsidersHolding?.toString(),
    minSnipersHolding: filter.minSnipersHolding?.toString(),
    maxSnipersHolding: filter.maxSnipersHolding?.toString(),
    minBundlersHolding: filter.minBundlersHolding?.toString(),
    maxBundlersHolding: filter.maxBundlersHolding?.toString(),
    minFee: filter.minFee?.toString(),
    maxFee: filter.maxFee?.toString(),
    providers:
      filter.providers === 'all' ? 'all' : [...filter.providers.values()],
    withMayhem: filter.withMayhem,
    withAtLeastOneSocial: filter.withAtLeastOneSocial,
    withTwitter: filter.withTwitter,
    withTelegram: filter.withTelegram,
    withWebsite: filter.withWebsite,
  } satisfies ExploreFilterJSON)
}

export interface ExploreFilterJSON {
  keywords: Array<string> | undefined
  maxHolders: string | undefined
  minHolders: string | undefined
  minTx: string | undefined
  maxTx: string | undefined
  maxLiquidityInUsd: string | undefined
  minLiquidityInUsd: string | undefined
  maxVolumeInUsd: string | undefined
  minVolumeInUsd: string | undefined
  maxMarketCap: string | undefined
  minMarketCap: string | undefined
  maxBoosters: string | undefined
  minBoosters: string | undefined
  maxSplashPoints: string | undefined
  minSplashPoints: string | undefined
  minDevHolding: string | undefined
  maxDevHolding: string | undefined
  minTop10Holding: string | undefined
  maxTop10Holding: string | undefined
  minInsidersHolding: string | undefined
  maxInsidersHolding: string | undefined
  minSnipersHolding: string | undefined
  maxSnipersHolding: string | undefined
  minBundlersHolding: string | undefined
  maxBundlersHolding: string | undefined
  providers: Array<string> | 'all' | undefined
  withMayhem: boolean | undefined
  minAge: string | undefined
  maxAge: string | undefined
  minFee: string | undefined
  maxFee: string | undefined
  withAtLeastOneSocial: true | undefined
  withTwitter: true | undefined
  withTelegram: true | undefined
  withWebsite: true | undefined
}

export const fromJSON = (json: string): ExploreFilter =>
  pipe(
    DomUtil.parseJsonUnsafe<ExploreFilterJSON | undefined>(json),
    Result.map((x): ExploreFilter => {
      const providers = (() => {
        if (Array.isArray(x?.providers)) {
          return pipe(
            x.providers,
            Array.map((it) => Provider.byId[it as Provider.Id]),
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            Array.filter((it) => it !== undefined),
            Array.map((it) => it.id),
            Set.create,
          )
        }
        return 'all' as const
      })()

      return {
        keywords: x?.keywords ?? [],
        maxHolders: x?.maxHolders ? BigNumber(x.maxHolders) : undefined,
        minHolders: x?.minHolders ? BigNumber(x.minHolders) : undefined,
        minTx: x?.minTx ? BigNumber(x.minTx) : undefined,
        maxTx: x?.maxTx ? BigNumber(x.maxTx) : undefined,
        maxLiquidityInUsd: x?.maxLiquidityInUsd
          ? BigNumber(x.maxLiquidityInUsd)
          : undefined,
        minLiquidityInUsd: x?.minLiquidityInUsd
          ? BigNumber(x.minLiquidityInUsd)
          : undefined,
        maxVolumeInUsd: x?.maxVolumeInUsd
          ? BigNumber(x.maxVolumeInUsd)
          : undefined,
        minVolumeInUsd: x?.minVolumeInUsd
          ? BigNumber(x.minVolumeInUsd)
          : undefined,
        maxMarketCap: x?.maxMarketCap ? BigNumber(x.maxMarketCap) : undefined,
        minMarketCap: x?.minMarketCap ? BigNumber(x.minMarketCap) : undefined,
        maxBoosters: x?.maxBoosters ? BigNumber(x.maxBoosters) : undefined,
        minBoosters: x?.minBoosters ? BigNumber(x.minBoosters) : undefined,
        maxSplashPoints: x?.maxSplashPoints
          ? BigNumber(x.maxSplashPoints)
          : undefined,
        minSplashPoints: x?.minSplashPoints
          ? BigNumber(x.minSplashPoints)
          : undefined,
        minDevHolding: x?.minDevHolding
          ? BigNumber(x.minDevHolding)
          : undefined,
        maxDevHolding: x?.maxDevHolding
          ? BigNumber(x.maxDevHolding)
          : undefined,
        minTop10Holding: x?.minTop10Holding
          ? BigNumber(x.minTop10Holding)
          : undefined,
        maxTop10Holding: x?.maxTop10Holding
          ? BigNumber(x.maxTop10Holding)
          : undefined,
        minInsidersHolding: x?.minInsidersHolding
          ? BigNumber(x.minInsidersHolding)
          : undefined,
        maxInsidersHolding: x?.maxInsidersHolding
          ? BigNumber(x.maxInsidersHolding)
          : undefined,
        minSnipersHolding: x?.minSnipersHolding
          ? BigNumber(x.minSnipersHolding)
          : undefined,
        maxSnipersHolding: x?.maxSnipersHolding
          ? BigNumber(x.maxSnipersHolding)
          : undefined,
        minBundlersHolding: x?.minBundlersHolding
          ? BigNumber(x.minBundlersHolding)
          : undefined,
        maxBundlersHolding: x?.maxBundlersHolding
          ? BigNumber(x.maxBundlersHolding)
          : undefined,
        minAge: x?.minAge ? NumberUtil.safeParseFloat(x.minAge) : undefined,
        maxAge: x?.maxAge ? NumberUtil.safeParseFloat(x.maxAge) : undefined,
        minFee: x?.minFee ? NumberUtil.parseBigNumber(x.minFee) : undefined,
        maxFee: x?.maxFee ? NumberUtil.parseBigNumber(x.maxFee) : undefined,
        providers,
        withMayhem: x?.withMayhem ?? false,
        withAtLeastOneSocial: x?.withAtLeastOneSocial ?? undefined,
        withTwitter: x?.withTwitter ?? undefined,
        withTelegram: x?.withTelegram ?? undefined,
        withWebsite: x?.withWebsite ?? undefined,
      }
    }),
    Result.getOrElse(() => EMPTY),
  )

export const toPoolFiltersDto = (
  filter: ExploreFilter,
  sorting?: { sortBy?: PoolsSortBy; sortDir?: 'asc' | 'desc' },
  period?: PairMetrics.MetricsSpan,
): ExplorePoolFiltersDto => {
  const onlyMayhem =
    Set.is(filter.providers) && filter.providers.size === 0 && filter.withMayhem

  const supportedDex = (() => {
    if (filter.providers === 'all' || onlyMayhem) {
      return undefined
    }

    return [...filter.providers.values()] as Array<SupportedDex>
  })()

  return {
    period,
    supportedDex: supportedDex,
    keywords: filter.keywords.length === 0 ? undefined : filter.keywords,
    minLiquidityInUsd: filter.minLiquidityInUsd?.toString(),
    maxLiquidityInUsd: filter.maxLiquidityInUsd?.toString(),
    minVolumeInUsd: filter.minVolumeInUsd?.toString(),
    maxVolumeInUsd: filter.maxVolumeInUsd?.toString(),
    minMarketCapInUsd: filter.minMarketCap?.toString(),
    maxMarketCapInUsd: filter.maxMarketCap?.toString(),
    minBoosters: filter.minBoosters?.toString(),
    maxBoosters: filter.maxBoosters?.toString(),
    minSplashPoints: filter.minSplashPoints?.toString(),
    maxSplashPoints: filter.maxSplashPoints?.toString(),
    minHolders: filter.minHolders?.toString(),
    maxHolders: filter.maxHolders?.toString(),
    minTx: filter.minTx?.toString(),
    maxTx: filter.maxTx?.toString(),
    createdBefore: filter.minAge
      ? DateF.subMinutes(new Date(), filter.minAge).toISOString()
      : undefined,
    createdAfter: filter.maxAge
      ? DateF.subMinutes(new Date(), filter.maxAge).toISOString()
      : undefined,
    sortBy: sorting?.sortBy,
    sortDir: sorting?.sortDir,
    minDevHolding: filter.minDevHolding?.toString(),
    maxDevHolding: filter.maxDevHolding?.toString(),
    minTop10Holding: filter.minTop10Holding?.toString(),
    maxTop10Holding: filter.maxTop10Holding?.toString(),
    minInsidersHolding: filter.minInsidersHolding?.toString(),
    maxInsidersHolding: filter.maxInsidersHolding?.toString(),
    minSnipersHolding: filter.minSnipersHolding?.toString(),
    maxSnipersHolding: filter.maxSnipersHolding?.toString(),
    minBundlersHolding: filter.minBundlersHolding?.toString(),
    maxBundlersHolding: filter.maxBundlersHolding?.toString(),
    minFee: filter.minFee?.toString(),
    maxFee: filter.maxFee?.toString(),
    hasAtLeastOneSocial: filter.withAtLeastOneSocial,
    hasTwitter: filter.withTwitter,
    hasTelegram: filter.withTelegram,
    hasWebsite: filter.withWebsite,
    onlyMayhem: onlyMayhem,
    withMayhem: onlyMayhem ? undefined : filter.withMayhem,

    audit: undefined,
  }
}

export const eq: Equivalence.Equivalence<ExploreFilter> = (left, right) => {
  for (const property of Object.keys(left)) {
    const leftValue = left[property as keyof ExploreFilter]
    const rightValue = right[property as keyof ExploreFilter]
    if (BigNumber.isBigNumber(leftValue) && BigNumber.isBigNumber(rightValue)) {
      if (!leftValue.eq(rightValue)) {
        return false
      }
      continue
    }
    if (
      leftValue instanceof globalThis.Set &&
      rightValue instanceof globalThis.Set
    ) {
      if (!Set.equals(leftValue, rightValue)) {
        return false
      }
      continue
    }

    if (Array.isArray(leftValue) && Array.isArray(rightValue)) {
      if (leftValue.join(',') !== rightValue.join(',')) {
        return false
      }
      continue
    }

    if (DateF.isDate(leftValue) && DateF.isDate(rightValue)) {
      if (!DateF.isEqual(leftValue, rightValue)) {
        return false
      }
      continue
    }

    if (leftValue !== rightValue) {
      return false
    }
  }
  return true
}
